import { z } from 'zod';

import { listQuerySchema } from './common.js';
import {
  connectionStatusSchema,
  orderFinancialStatusSchema,
  orderFulfillmentStatusSchema,
  productStatusSchema,
  type ConnectionStatus,
  type OrderFinancialStatus,
  type OrderFulfillmentStatus,
  type ProductStatus,
  StorePlatform,
} from './enums.js';

/**
 * COMMERCE — mağaza bağlama, katalog, sipariş, stok (Genişleme Adım 1).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 Neden `integrations.ts`ten AYRI dosya
 * ═══════════════════════════════════════════════════════════════════════════
 * Aynı sözcükler kullanılıyor (bağlantı, jeton, senkron, sağlık) ama
 * ANLATTIKLARI şey farklı ve tek dosyada tutmak iki kavramı birbirine
 * karıştırırdı:
 *
 *   • Reklam hesabının bir HİYERARŞİSİ var (kampanya → ad set → reklam) ve
 *     gün bazlı METRİĞİ var. Mağazanın ikisi de yok.
 *   • Mağazanın bir KATALOĞU var (ürün → varyant) ve OLAY akışı var
 *     (sipariş, stok hareketi). Reklam hesabının ikisi de yok.
 *   • Reklam tarafında yazma işlemi PARA HARCATIR (bütçe, durum). Mağaza
 *     tarafında yazma işlemi bu adımda HİÇ YOK — yalnız okuma.
 *
 * `StoreConnection` tablosunun `AdConnection`dan ayrı olmasının gerekçesi de
 * bu (Genişleme planı Adım 1). Aynı tabloya sıkıştırmak `AdConnection`ın
 * yarı alanını nullable yapardı ve "bu satır mağaza mı reklam hesabı mı"
 * sorusunu her sorguya taşırdı.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 KİŞİSEL VERİ BU ALANA GİRMİYOR — bilinçli kapsam kararı
 * ═══════════════════════════════════════════════════════════════════════════
 * Sipariş yükleri müşteri adı, e-posta, telefon ve teslimat adresi taşıyor.
 * Hiçbiri normalize edilmiyor, hiçbiri saklanmıyor. Saklanan tek müşteri
 * bilgisi `customerRef` — platformun kendi opak kimliği (Shopify'da bir GID).
 *
 * Sebep: bu adımın ve Adım 3'ün ihtiyacı olan HİÇBİR soru kişisel veri
 * istemiyor. "Hangi ürün ne kadar sattı", "stok bitti mi", "yeni müşteri mi
 * tekrar eden mi" — üçü de `customerRef` ve satırlarla cevaplanıyor. Kişisel
 * veriyi "belki lazım olur" diye çekmek, KVKK/GDPR yüzeyini ve silme
 * yükümlülüğünü bedavaya satın almak olurdu.
 *
 * ⚠️ Bir gün sipariş bildirimi ya da müşteri segmenti istenirse bu karar
 * yeniden açılır — ama o zaman AÇIKÇA, saklama süresi ve silme yolu ile
 * birlikte. `leads.ts`teki desen emsal.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Senkron pencereleri
// ═══════════════════════════════════════════════════════════════════════════

/**
 * İlk bağlanışta kaç günlük SİPARİŞ geçmişi çekilir (kullanıcı kararı,
 * 13 Ağu 2026).
 *
 * Yalnız siparişe uygulanır. Ürün, varyant ve stok her zaman TAMAMEN çekilir:
 * katalogda "son 90 gün" diye bir şey yok — iki yıl önce açılmış ve hâlâ
 * satan bir ürün pencerenin dışında kalırsa katalog eksik olur, eski değil.
 *
 * Neden 90: bir çeyrek, Adım 3'ün ürün performans raporuna anlamlı bir taban
 * veriyor ve Shopify'ın maliyet tabanlı GraphQL kotasında sayfalama ile tek
 * seferde iniyor — yani Bulk Operations hattı yazmayı zorunlu kılmıyor.
 */
export const STORE_HISTORY_DAYS = 90;

/**
 * 🔴 SHOPIFY'IN 60 GÜN DUVARI — `STORE_HISTORY_DAYS` tek başına yetmiyor.
 *
 * `read_orders` kapsamı mağazanın YALNIZ SON 60 GÜNÜNÜ gösteriyor. Daha
 * eskisi için `read_all_orders` gerekiyor ve o bir "korumalı kapsam":
 * Shopify uygulama incelemesinden geçmeden verilmiyor.
 *
 * ⚠️ Bunun en tehlikeli yanı SESSİZ olması: Shopify hata döndürmüyor, daha
 * eski siparişleri sonuç kümesinden çıkarıp `hasNextPage: false` diyor.
 * Yani 90 gün isteyen bir senkron 60 gün alır, `truncated` bayrağı `false`
 * kalır ve hiçbir yerde uyarı çıkmaz — kullanıcı üç aylık raporun eksik
 * olduğunu asla öğrenmez.
 *
 * Bu yüzden senkron, bağlantının GERÇEK kapsamlarına bakıp pencereyi
 * kendisi kısıyor ve farkı `lastError`a yazıyor (`syncOrders`). Kapsam
 * varsa kısıtlama uygulanmıyor.
 *
 * 🔴 `read_all_orders` istenen kapsam listesine EKLENMEDİ ve bu bilinçli:
 * onaylanmamış bir korumalı kapsam istemek kurulumun tamamını reddettirir.
 * Yani eklemek "90 gün gelir" değil "hiç bağlanamaz" demekti. Uygulama
 * incelemesi geçildiğinde `SHOPIFY_SCOPES`a eklenecek ve bu sabit
 * kendiliğinden etkisizleşecek.
 */
export const SHOPIFY_ORDER_SCOPE_WINDOW_DAYS = 60;

/// `read_all_orders` — 60 gün duvarını kaldıran korumalı kapsam.
export const SHOPIFY_ALL_ORDERS_SCOPE = 'read_all_orders';

/**
 * Zamanlanmış senkronda kaç günlük sipariş yeniden okunur.
 *
 * `BACKFILL_DAYS` (reklam tarafı, 7) ile aynı gerekçe ve neredeyse aynı
 * değer: platformlar geçmişi geriye dönük REVİZE ediyor. Siparişte revizyon
 * iade, iptal ve kısmi gönderi olarak geliyor ve bunlar günler sonra
 * düşebiliyor. Webhook varken bile gerekli — webhook kaçabilir, bu ağ.
 */
export const STORE_BACKFILL_DAYS = 7;

/**
 * Tek senkron koşusunda yazılacak EN FAZLA sipariş.
 *
 * 🔴 SESSİZ KIRPMA DEĞİL: sınır aşılırsa kaç siparişin dışarıda kaldığı
 * loglanıyor ve senkron `truncated` bayrağıyla dönüyor. Sessiz kırpma "90
 * günün tamamını çektik" diye okunurdu.
 *
 * Sınır neden var: ilk senkron 90 günü çekiyor ve günde binlerce sipariş alan
 * bir mağazada bu tek koşuda on binlerce satır demek. Kuyruk işinin süresi
 * sınırsız değil ve yarıda kesilen bir koşu hiçbir çıpa ilerletmediği için
 * baştan başlardı — yani asla bitmezdi.
 */
export const STORE_ORDER_PAGE_CAP = 10_000;

// ═══════════════════════════════════════════════════════════════════════════
// Kimlik bilgisi — OAuth ve satıcı anahtarı AYNI kolonda
// ═══════════════════════════════════════════════════════════════════════════

/**
 * 🔴 Mağaza kimlik bilgisi TEK bir şifreli blob'da (`credentialsEnc`), Ad-
 * `Connection`daki gibi `accessTokenEnc` / `refreshTokenEnc` kolon çiftinde
 * DEĞİL.
 *
 * Sebep ölçülmüş bir gereklilik, tercih değil: Adım 2 (Trendyol · İkas ·
 * Ticimax) genişleme planında **şemaya dokunmayan** bir adım olarak
 * planlandı. O üç sağlayıcının hiçbiri OAuth kullanmıyor — satıcı başına API
 * anahtarı + gizli anahtar + satıcı kimliği taşıyorlar. Kolon çiftiyle
 * gitseydik Adım 2 en az üç yeni nullable kolon (`apiKeyEnc`, `apiSecretEnc`,
 * `sellerRef`) ve bir migration isterdi — yani şema-serbest olmaktan çıkardı
 * ve dalga planı bozulurdu.
 *
 * Blob'un bedeli: kimlik bilgisi alanlarıyla SORGU yapılamaz. Yapılmıyor da —
 * jetonla filtreleyen bir sorgu zaten olmamalı. Sorgu gereken tek alan
 * (`tokenExpiresAt`) blob'un DIŞINDA, ayrı kolonda duruyor.
 *
 * Şifreleme `TOKEN_VAULT_KEY` ile AES-256-GCM, `accessTokenEnc` ile aynı
 * fonksiyon ve aynı sürüm öneki (`v1:…`).
 *
 * ⚠️ Çözülmüş kimlik bilgisi HİÇBİR DTO'ya, loga ya da hata mesajına girmez.
 */
export const shopifyCredentialsSchema = z.object({
  kind: z.literal('shopify'),
  /// Shopify Admin API jetonu. 🔴 SÜRESİ YOK — Shopify offline access token
  /// kalıcı verir ve yenileme yolu YOKTUR. Kullanıcı uygulamayı kaldırırsa
  /// jeton anında ölür; `refreshToken` denemek anlamsız, doğru cevap
  /// yeniden kurulum.
  accessToken: z.string().min(1),
});

/**
 * Satıcı başına anahtar taşıyan pazaryerleri (Adım 2 için önden tanımlı).
 *
 * Bu şema BUGÜN kullanılmıyor ve bilinçli: Adım 2 kendi adapter'ını yazarken
 * `storeCredentialsSchema` birleşimine dokunmadan bunu doldurabilsin diye
 * şekil şimdiden sabitlendi. Alanların hepsi opsiyonel çünkü üç sağlayıcının
 * üçü de farklı alt kümesini kullanıyor (Trendyol: apiKey+apiSecret+sellerId,
 * Ticimax: tek `apiKey`, İkas: clientId+clientSecret).
 */
export const apiKeyCredentialsSchema = z.object({
  kind: z.literal('apiKey'),
  apiKey: z.string().min(1),
  apiSecret: z.string().min(1).optional(),
  /// Satıcı/mağaza kimliği (Trendyol `supplierId`). Sır DEĞİL ama kimlik
  /// bilgisiyle birlikte yaşıyor; ayrı kolona koymak iki yerden okumak olurdu.
  sellerId: z.string().min(1).optional(),
});

/**
 * SÜRELİ OAuth2 jetonu — Amazon SP-API ve Etsy (Adım 11 sonrası).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 Neden ÜÇÜNCÜ bir şekil — eldeki ikisinden neden biri yetmedi
 * ═══════════════════════════════════════════════════════════════════════════
 * Amazon SP-API ve Etsy'nin ikisi de OAuth2 kullanıyor ve ikisi de SÜRELİ
 * erişim jetonu + yenileme jetonu taşıyor. Mevcut iki şekil bunu
 * anlatamıyor, ve eksiklikleri birbirinin tersi:
 *
 *   shopify → jeton KALICI, `refreshToken` diye bir alanı YOK. Amazon'un
 *             jetonunu buraya koysaydık ~1 saat sonra ölürdü ve elimizde onu
 *             yenileyecek HİÇBİR ŞEY olmazdı: bağlantı saat başı
 *             kendiliğinden DISCONNECTED'a düşer, kullanıcı her gün yeniden
 *             bağlanırdı.
 *   apiKey  → SÜRE kavramı yok ve alanlarının hiçbiri jeton değil. Yenileme
 *             jetonunu `apiSecret`e sıkıştırmak teknik olarak mümkündü; o
 *             zaman aynı alan bazen "sır" bazen "yenileme jetonu" anlamına
 *             gelirdi ve `store-sync.service.ts` içindeki şekil kontrolü
 *             (`credentials.kind !== 'apiKey'`) YANLIŞ şekli DOĞRU sanardı —
 *             yani kontrolün yakalamak için var olduğu hatayı görmezdi.
 *
 * 🔴 MEVCUT ŞEKLİ GENİŞLETMEK yapılmadı. `shopifyCredentialsSchema`ya
 * opsiyonel bir `refreshToken` eklemek en kısa yoldu ve yanlış olurdu:
 * opsiyonel alan hiçbir şey GARANTİ ETMEZ. Amazon adapter'ı jetonu
 * `string | undefined` olarak görür ve "yenileme jetonu yoksa ne yapayım"
 * sorusunu her çağrı yerinde yeniden cevaplamak zorunda kalırdı. Ayrı bir
 * `kind` ile o soru DERLEME zamanında bitiyor: `kind === 'oauth2'` gören kod,
 * süreli ve yenilenebilir bir jetonla konuştuğunu bilir.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⏱️ SÜRE BU BLOB'DA DEĞİL — `store_connections.tokenExpiresAt` kolonunda
 * ═══════════════════════════════════════════════════════════════════════════
 * Buraya bir `expiresAt` alanı KONMADI ve bu, dosyanın başındaki blob
 * kararının doğrudan sonucu: "sorgu gereken tek alan (`tokenExpiresAt`)
 * blob'un DIŞINDA". Süre şifreli blob'un içine girseydi, "hangi bağlantının
 * jetonu bir saat içinde ölüyor" sorusu bir SQL sorgusuyla cevaplanamaz,
 * bütün satırların çözülmesini gerektirirdi.
 *
 * `StoreCredentialResult.expiresAt` zaten ayrı bir alan ve `store-oauth.service.ts`
 * onu doğrudan `tokenExpiresAt` kolonuna yazıyor — yani yol AÇIK, yeni bir
 * şey gerekmiyor.
 */
export const oauth2CredentialsSchema = z.object({
  kind: z.literal('oauth2'),
  /// Platforma giden `Authorization: Bearer …` jetonu. SÜRELİ — ne kadar
  /// süreli olduğu burada DEĞİL, `tokenExpiresAt` kolonunda (yukarıdaki
  /// gerekçe).
  accessToken: z.string().min(1),
  /**
   * Yenileme jetonu — OPSİYONEL, ve bu bir kolaylık değil bir gerçek.
   *
   * ⚠️ ÖLÇÜLMEDİ: Amazon LWA'nın yenileme jetonunun kalıcı mı yoksa her
   * yenilemede DÖNEN yeni bir jeton mu olduğu canlı bir hesaba karşı
   * doğrulanmadı. İkisi de mümkün ve fark önemli: dönüyorsa her yenilemede
   * kasaya YAZMAK zorundayız, yazmazsak bir sonraki yenileme 400 alır.
   * Bu yüzden `verifyCredentials` sözleşmesi "tazelenmiş kimlik dönerse
   * kasaya yaz" diyor ve adapter dönen jetonu AYNEN geri vermeli.
   *
   * `undefined` olabilmesinin sebebi: bazı sağlayıcılar yalnız kısa ömürlü
   * erişim jetonu veriyor ve yenileme için baştan yetkilendirme istiyor.
   * O durumda alan YOK — boş dize DEĞİL. Boş dize kabul edilseydi
   * `if (credentials.refreshToken)` kontrolü onu "var ama boş" diye geçirir
   * ve platforma `refresh_token=` diye boş bir gövde giderdi; hata "400
   * invalid_grant" olurdu, yani asıl sebebi (jetonun hiç olmaması)
   * göstermeyen bir cevap. `min(1)` tam olarak bunu kapatıyor.
   */
  refreshToken: z.string().min(1).optional(),
  /**
   * "Hangi mağaza" — `apiKeyCredentialsSchema.sellerId` ile AYNI alan adı,
   * AYNI anlam ve aynı gerekçeyle opsiyonel.
   *
   * ⚠️ ÖLÇÜLMEDİ: Amazon'da bunun `sellingPartnerId` (merchant token) mi
   * yoksa `marketplaceId` mi olacağı, Etsy'de `shop_id`nin jetonla birlikte
   * mi yoksa ayrı bir çağrıyla mı geldiği doğrulanmadı — eski APEX
   * projesinin Amazon/Etsy adapter'ları hiç bağlanmamış. Yanlış çıkarsa
   * sonucu: adapter kendi keşif çağrısında doğru kimliği bulur ve buraya
   * yazar; şema değişmez çünkü alan zaten "hangi mağaza" diyor, "hangi
   * ALANIN adı" demiyor.
   *
   * Opsiyonel çünkü bazı akışlarda jeton takası ile mağaza keşfi AYRI
   * çağrılar: jeton elimizde ama mağaza kimliği henüz yok olan bir an var.
   */
  sellerId: z.string().min(1).optional(),
  /**
   * Bölge — ⚠️ NEDEN BİRİNCİ SINIF ALAN, neden `metadata` kolonu değil
   * ═════════════════════════════════════════════════════════════════════════
   * Amazon SP-API'de bölge bir etiket değil ADRES: hem veri ucu
   * (`sellingpartnerapi-<bölge>.amazon.com`) hem de LWA jeton yenileme
   * çağrısı bölgeye göre değişiyor. Yani jetonu YENİLEMEK için bölgeyi
   * bilmek ŞART.
   *
   * 🔴 Yenilemenin yapılabileceği tek yer `StoreConnector.verifyCredentials`
   * ve imzası şu: `(credentials, store: StoreRef)`. `StoreRef` yalnız
   * `externalId` / `currency` / `timezone` taşıyor. Bölgeyi
   * `store_connections.metadata`ya koysaydık yenileme anında ona ERİŞEN
   * hiçbir yol olmazdı — connector DURUMSUZ ve veritabanını görmüyor
   * (`store-connector.types.ts` başlığı). Geriye iki seçenek kalıyordu:
   * `StoreRef`e alan eklemek (dört mevcut adapter'ın imzasını ve
   * `twin-files.spec.ts` ile korunan dosyaları hareket ettirir) ya da
   * bölgeyi kimlik bilgisiyle BİRLİKTE taşımak. İkincisi seçildi ve
   * kavramsal olarak da doğru: başka bölgenin jetonu o adreste zaten
   * geçersiz — bölge jetonun ayrılmaz parçası.
   *
   * 🔴 ŞEKİL süzgeci var, DEĞER listesi YOK. Bu değer URL'e giriyor ve
   * süzgeçsiz bırakmak yol kaçışı/SSRF demekti (`normalizeMerchantId`
   * emsali). Ama Amazon'un bölge listesi buraya YAZILMADI:
   *
   * ⚠️ ÖLÇÜLMEDİ: gerçek bölge kodlarının kümesi (`na`/`eu`/`fe` mi,
   * `us-east-1` mi) doğrulanmadı. Kapalı bir liste yazsaydık ve liste
   * eksik olsaydı, DOĞRU bölgeyi girmiş bir kullanıcı şemada reddedilir ve
   * gördüğü hata "kimlik bilgisi beklenen şekilde değil" olurdu — sebebi
   * göstermeyen bir cevap. Şema yalnız karakter kümesini daraltıyor; hangi
   * bölgenin gerçek olduğuna adapter KENDİ KAPALI eşleme tablosundan karar
   * verecek ve tanımadığı bölgede FIRLATACAK. Sessizce bir varsayılana
   * düşmek (`na`) yasak: AB satıcısının siparişlerini boş dönen bir uçtan
   * okumak, sıfır siparişle "başarılı" senkron üretmek olurdu.
   */
  region: z
    .string()
    .trim()
    .regex(/^[a-z0-9-]{1,32}$/, 'Bölge yalnız küçük harf, rakam ve tire içerebilir')
    .optional(),
});

/**
 * 🔴 `discriminatedUnion` — üçüncü şeklin eskileri BOZMAMASININ yapısal sebebi.
 *
 * Zod ayrıştırmayı `kind` alanına bakarak TEK bir alt şemaya yönlendiriyor;
 * `union` olsaydı sırayla dener ve hata mesajı "hiçbir varyant tutmadı"
 * olurdu. Somut sonucu: `kind: 'oauth2'` ama `accessToken` eksik bir nesnede
 * hata `accessToken` alanını GÖSTERİYOR, "üç şeklin üçü de tutmadı" demiyor.
 * Mevcut Shopify ve dört pazaryeri satırı bu eklemeden hiç etkilenmiyor —
 * `store-credentials.spec.ts` bunu ayrıca kanıtlıyor.
 *
 * ⚠️ Adapter'lar tek bir şekle daralırken `Extract<StoreCredentials,
 * { kind: 'oauth2' }>` kullanıyor — `store-sync.service.ts`in `apiKey` için
 * yaptığının aynısı. Her varyant için ayrı bir dışa aktarılan tip adı
 * ÜRETİLMEDİ: `ShopifyCredentials` tam da öyle doğdu ve bugün onu kullanan
 * tek bir satır yok.
 */
export const storeCredentialsSchema = z.discriminatedUnion('kind', [
  shopifyCredentialsSchema,
  apiKeyCredentialsSchema,
  oauth2CredentialsSchema,
]);
export type StoreCredentials = z.infer<typeof storeCredentialsSchema>;
export type ShopifyCredentials = z.infer<typeof shopifyCredentialsSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Adapter sınırı — normalize şekiller
// ═══════════════════════════════════════════════════════════════════════════

/**
 * 🔴 Platforma özgü HİÇBİR kavram bu şekillerin dışına çıkmaz.
 *
 * `gid://shopify/Product/123`, `inventory_item_id`, `barındırma konumu`,
 * `supplierId` — hepsi `StoreConnector` implementasyonlarının içinde kalır.
 * Üst katmanlar (Adım 3'ün katalog zekâsı, banner stüdyosu, raporlar)
 * yalnızca aşağıdaki tipleri görür.
 *
 * `AdPlatformConnector` ile aynı kural ve aynı gerekçe: sınır tutulursa yeni
 * bir mağaza platformu = bir dosya. Tutulmazsa `if (platform === 'SHOPIFY')`
 * dalları on beş dosyaya yayılır.
 */

/// Mağaza platformunun neyi verip neyi vermediği. Arayüz kartı ve senkron
/// adımları buna bakar — "destekleniyor mu" sorusu koda gömülmez.
export interface StoreConnectorCapabilities {
  platform: StorePlatform;
  /// Gerçek implementasyon var mı. Adım 2 gelene kadar SHOPIFY dışında `false`.
  available: boolean;
  /// `available: false` ise nedeni — i18n anahtarı, düz metin DEĞİL.
  unavailableReasonKey: string | null;
  canReadProducts: boolean;
  canReadOrders: boolean;
  /**
   * Konum kırılımlı stok okuyabiliyor mu.
   *
   * ⚠️ `canReadProducts`ın alt kümesi DEĞİL, ayrı bir soru: varyantın toplam
   * stok adedi katalogla birlikte gelir, hangi DEPODA kaç tane olduğu ayrı
   * bir uçtur ve her platformda yoktur. Tek depolu bir pazaryerinde `false`
   * dönmesi eksiklik değil, o platformda konum kavramının olmaması.
   */
  canReadInventoryLevels: boolean;
  /**
   * Webhook kurabiliyor muyuz.
   *
   * `false` dönen platformlarda tazelik YALNIZ zamanlanmış senkrondan gelir
   * ve arayüz bunu söylemeli — "stok değişince anında görürsünüz" cümlesi
   * o platformlarda yalan olur.
   */
  canRegisterWebhooks: boolean;
  /**
   * Katalogda YAZMA (başlık/açıklama düzeltme) yapabiliyor mu.
   *
   * Shopify'da Adım 3 ile `true` — `write_products` kapsamı istendi ve
   * `updateProduct` metodu yazıldı. Diğer platformlarda hâlâ `false`.
   *
   * 🔴 `true` OLMASI "AI ne isterse yazar" DEMEK DEĞİL. Yazma yolunun TEK
   * girişi `agent` alanının onay kapısı: öneri `AWAITING_APPROVAL` durumunda
   * bir `WorkflowActionLog` satırı olarak doğuyor ve kullanıcı onaylamadan
   * mağazaya tek bir istek gitmiyor (`agent-proposal.service.ts` başlığı).
   * İkinci bir uygulama yolu AÇILMADI.
   */
  canWriteCatalog: boolean;
}

/**
 * AI düzeltmesinin mağazaya yazacağı alanlar — Genişleme Adım 3.
 *
 * 🔴 LİSTE KAPALI ve dar tutuldu. Yalnız METİN alanları var: fiyat, stok,
 * görsel ve yayın durumu BİLEREK yok. Bunlar ticari kararlar ve bir dil
 * modelinin önerisiyle değiştirilmeleri, kullanıcının onay kutusunda
 * göremeyeceği kadar büyük sonuçlar doğurur.
 *
 * `undefined` = "bu alana dokunma". `null` YOK: bir alanı BOŞALTMAK
 * düzeltme değil silmedir ve bu akışın işi değil.
 */
export interface CatalogProductPatch {
  title?: string;
  description?: string;
  productType?: string;
  tags?: string[];
}

/// Bağlanmadan önce keşfedilen mağaza. Shopify'da tek elemanlı (jeton zaten
/// TEK mağazaya ait); pazaryerlerinde satıcı hesabı birden çok mağaza
/// taşıyabiliyor.
export interface DiscoveredStore {
  /// Platformun kimliği. Shopify: `magazam.myshopify.com`.
  externalId: string;
  name: string;
  /// Mağazanın ana para birimi — sipariş satırlarına yazılıyor.
  currency: string;
  timezone: string;
  /// Vitrin adresi. Katalog ekranında ürüne gitmek için kullanılıyor.
  domain: string | null;
}

/// Jeton/anahtar takasının sonucu. `ConnectorTokenResult`ün mağaza eşi.
export interface StoreCredentialResult {
  credentials: StoreCredentials;
  /// Kimlik bilgisi sürelİYSE. Shopify'da `null` — jeton kalıcı.
  expiresAt: Date | null;
  /// Platformun GERÇEKTEN verdiği kapsamlar. İstediğimizle aynı olmayabilir.
  scopes: string[];
  store: DiscoveredStore;
}

/**
 * Katalog ürünü.
 *
 * `description` DÜZ METİN olarak taşınıyor, HTML olarak değil: Shopify
 * `descriptionHtml` veriyor ve onu saklamak, Adım 3'ün "açıklama zayıf mı"
 * kuralına etiket saymayı ve arayüze `dangerouslySetInnerHTML` koymayı
 * dayatırdı. Dönüşüm adapter'ın İÇİNDE.
 */
export interface NormalizedProduct {
  platformRef: string;
  title: string;
  description: string | null;
  /// Vitrin URL'inin slug'ı (`yaz-elbisesi`). Adım 3 bunu SEO kuralında
  /// kullanıyor; tam adresi `onlineStoreUrl` taşıyor.
  handle: string | null;
  productType: string | null;
  vendor: string | null;
  tags: string[];
  status: ProductStatus;
  /// Kapak görseli. `null` = ürünün HİÇ görseli yok → Adım 3'ün katalog
  /// sağlık kuralının en ağır maddesi.
  imageUrl: string | null;
  /// Toplam görsel sayısı. Kapak varken bile 1 olması "tek açıdan çekilmiş"
  /// demek ve ayrı bir sağlık maddesi — bu yüzden ayrı alan.
  imageCount: number;
  onlineStoreUrl: string | null;
  publishedAt: string | null;
  /// Platformun bildirdiği son değişiklik anı. Artımlı senkronun çıpası bu
  /// alanla KARŞILAŞTIRILMIYOR (sorgu tarafında `updated_at` süzgeci var);
  /// saklanmasının sebebi "bu satır ne kadar bayat" sorusuna cevap vermek.
  platformUpdatedAt: string | null;
}

/**
 * Ürün varyantı — satılan asıl şey.
 *
 * 🔴 Sipariş satırı ÜRÜNE değil VARYANTA bağlanır ve fiyat burada durur.
 * Varyantsız bir ürün de Shopify'da varyantlıdır (tek, "Default Title"
 * adında); adapter o durumu gizlemez — tek varyantlı ürün olarak taşır.
 * Gizleseydik "ürünün fiyatı" diye bir alan uydurmak ve iki fiyat kaynağını
 * senkron tutmak gerekirdi.
 */
export interface NormalizedVariant {
  platformRef: string;
  productPlatformRef: string;
  title: string;
  sku: string | null;
  barcode: string | null;
  /// Mağazanın ana para biriminde. Kuruş DEĞİL, ana birim (Shopify zaten
  /// ondalık dize veriyor; çeviri gerekiyorsa adapter'ın içinde).
  price: number;
  /// Üstü çizili fiyat. `null` = indirim yok.
  compareAtPrice: number | null;
  position: number;
  /**
   * Stok kaydının platform kimliği (Shopify `InventoryItem` GID).
   *
   * Varyant kimliğinden AYRI ve bu ayrım zorunlu: stok webhook'u
   * (`inventory_levels/update`) varyant kimliği DEĞİL stok kalemi kimliği
   * gönderiyor. Bu alanı saklamazsak gelen webhook'u hiçbir varyantla
   * eşleştiremez ve sessizce atardık.
   */
  inventoryItemRef: string | null;
  /// Tüm konumların TOPLAMI. Konum kırılımı `NormalizedInventoryLevel`da.
  /// `null` = platform stok takibi yapmıyor (dijital ürün, hizmet) — `0`
  /// ile aynı şey DEĞİL, `0` "tükendi" demek.
  inventoryQuantity: number | null;
  imageUrl: string | null;
  platformUpdatedAt: string | null;
}

/**
 * Sipariş satırı.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 BAŞLIK, SKU VE FİYAT SATIRDA KOPYA DURUYOR — kasıtlı denormalizasyon
 * ═══════════════════════════════════════════════════════════════════════════
 * Ürün silinebilir, yeniden adlandırılabilir, fiyatı değişebilir. Sipariş
 * satırı SATIŞ ANINI anlatan bir kayıt: üç ay önceki satışı bugünün fiyatıyla
 * raporlamak yanlış olurdu, silinmiş ürünün satırını "bilinmeyen ürün" diye
 * göstermek ise Adım 3'ün ürün raporunda kalıcı bir boşluk açardı.
 *
 * Bu yüzden `productPlatformRef` bir JOIN kolaylığı, satırın kimliği değil.
 */
export interface NormalizedOrderLine {
  platformRef: string;
  /// Ürün platformda silinmişse `null` — satır yine de yazılır.
  productPlatformRef: string | null;
  variantPlatformRef: string | null;
  /// Satış anındaki ad. Ürün adı sonradan değişse bile bu satır değişmez.
  title: string;
  sku: string | null;
  quantity: number;
  /// Satış anındaki birim fiyat, indirimden ÖNCE.
  unitPrice: number;
  /// Bu satıra düşen indirim TUTARI (yüzde değil).
  discount: number;
  /// `unitPrice × quantity − discount`. Adapter hesaplamıyor, platformun
  /// verdiğini taşıyor: yuvarlama kuralları platform başına farklı ve bizim
  /// hesabımız toplamla tutmayabilir.
  totalPrice: number;
}

/**
 * Sipariş.
 *
 * ⚠️ `customerRef` DIŞINDA müşteri bilgisi YOK — dosyanın başındaki kişisel
 * veri kararı.
 */
export interface NormalizedOrder {
  platformRef: string;
  /// Kullanıcının gördüğü numara (`#1042`). `platformRef` ile aynı değil ve
  /// benzersiz olduğu GARANTİ EDİLMEZ — destek konuşmasında bu numara
  /// kullanılıyor, sorgu anahtarı olarak değil.
  orderNumber: string;
  placedAt: string;
  /// Siparişin KENDİ para birimi. Mağazanın ana para biriminden farklı
  /// olabilir (çok para birimli vitrin) ve toplamlar bu birimde.
  currency: string;
  totalPrice: number;
  subtotalPrice: number;
  totalTax: number;
  totalShipping: number;
  totalDiscount: number;
  /// İade edilen tutar. Ciro raporu bunu düşüyor; `financialStatus`
  /// `REFUNDED` olsa bile tutarı ayrıca taşımak gerekiyor çünkü kısmi iade
  /// durumu tek başına miktarı söylemiyor.
  totalRefunded: number;
  financialStatus: OrderFinancialStatus;
  fulfillmentStatus: OrderFulfillmentStatus;
  cancelledAt: string | null;
  /// Platformun opak müşteri kimliği. 🔴 Ad/e-posta/telefon DEĞİL.
  customerRef: string | null;
  /// Bu müşterinin kaçıncı siparişi. `1` = yeni müşteri. Platform vermiyorsa
  /// `null` — türetmeye ÇALIŞMIYORUZ, elimizdeki 90 günlük pencere "ilk
  /// sipariş" sorusuna cevap veremez ve yanlış cevap vermek boş bırakmaktan
  /// kötü.
  customerOrderIndex: number | null;
  /// Satış kanalı (`web`, `pos`, `Instagram`). Serbest metin: kanal listesi
  /// platform başına açık uçlu.
  channel: string | null;
  /// Sipariş kaynağı — reklam atfı için ilk kanca. Adım 3 ürün↔reklam
  /// köprüsünü kurarken buradan başlayacak (`utm_source` benzeri).
  referringSource: string | null;
  lines: NormalizedOrderLine[];
  platformUpdatedAt: string | null;
}

/**
 * Bir varyantın BİR KONUMDAKİ stok adedi.
 *
 * Ayrı bir şekil olmasının sebebi: "kaç tane var" ile "nerede var" farklı
 * sorular ve ikincisi kargo/ikmal kararlarını değiştiriyor. Toplamı
 * `NormalizedVariant.inventoryQuantity` taşıyor; bu şekil kırılımı.
 */
export interface NormalizedInventoryLevel {
  /// `NormalizedVariant.inventoryItemRef` ile eşleşir — varyant kimliği
  /// DEĞİL (gerekçe orada).
  inventoryItemRef: string;
  locationRef: string;
  locationName: string | null;
  /// Satılabilir adet. `null` = konum stok takibi yapmıyor.
  available: number | null;
  platformUpdatedAt: string | null;
}

/// Webhook'tan gelen ve DOĞRULANMIŞ olay. Taşıma katmanı (imza kontrolü,
/// gövde ayrıştırma) adapter'ın içinde; senkron yalnız bunu görür.
export type StoreWebhookTopic =
  | 'ORDER_CREATED'
  | 'ORDER_UPDATED'
  | 'ORDER_CANCELLED'
  | 'PRODUCT_UPDATED'
  | 'PRODUCT_DELETED'
  | 'INVENTORY_UPDATED'
  | 'APP_UNINSTALLED';

export interface StoreWebhookEvent {
  topic: StoreWebhookTopic;
  /// Olayın konusu olan kaydın platform kimliği.
  platformRef: string;
  /// Ayrıştırılmış yük — topic'e göre `NormalizedOrder`, `NormalizedProduct`
  /// ya da `NormalizedInventoryLevel`. `null` = platform yalnız kimlik
  /// gönderdi, kaydın kendisi çekilmeli.
  payload: NormalizedOrder | NormalizedProduct | NormalizedInventoryLevel | null;
}

// ═══════════════════════════════════════════════════════════════════════════
// Senkron özeti
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Bir mağaza senkron koşusunun sonucu.
 *
 * 🔴 Her adım AYRI sayılıyor ve her adımın hatası AYRI taşınıyor. Tek bir
 * `success: boolean` dönseydi "ürünler geldi, siparişler gelmedi" durumu
 * ya tamamen başarısız ya tamamen başarılı görünürdü; ikisi de yalan.
 * Bu, `SyncOutcome`taki `keywordError` / `leadError` ayrımının aynısı.
 */
export interface StoreSyncSummary {
  productsWritten: number;
  variantsWritten: number;
  ordersWritten: number;
  orderLinesWritten: number;
  inventoryLevelsWritten: number;
  /// Üstü bulunamadığı için ATLANAN satırlar. Sıfırdan büyükse loglanıyor —
  /// sessizce düşürmek "senkron başarılı, 0 varyant" diyen bir kaydın
  /// kullanıcıyı günlerce aratması demekti.
  variantsSkippedNoProduct: number;
  orderLinesSkippedNoOrder: number;
  inventorySkippedNoVariant: number;
  /// `STORE_ORDER_PAGE_CAP` aşıldı mı. `true` ise pencere TAM ÇEKİLMEDİ ve
  /// bu kullanıcıya söylenir.
  ordersTruncated: boolean;
}

export function emptyStoreSyncSummary(): StoreSyncSummary {
  return {
    productsWritten: 0,
    variantsWritten: 0,
    ordersWritten: 0,
    orderLinesWritten: 0,
    inventoryLevelsWritten: 0,
    variantsSkippedNoProduct: 0,
    orderLinesSkippedNoOrder: 0,
    inventorySkippedNoVariant: 0,
    ordersTruncated: false,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Uç sözleşmeleri
// ═══════════════════════════════════════════════════════════════════════════

/**
 * 🔴 `StorePlatform` SABİTİNDEN TÜRETİLİYOR — elle yazılmış liste DEĞİL.
 *
 * Adım 11'e kadar burada `z.enum(['SHOPIFY','TRENDYOL','IKAS','TICIMAX'])`
 * yazıyordu, yani aynı liste iki yerde yaşıyordu. Enum'a yedi değer eklenince
 * bu şema eskisiyle kaldı ve `commerce.service.ts` derleme hatası verdi —
 * bu SEFER şanslıydık, hata derleme zamanında çıktı. Ama şemanın kendisi
 * çalışma zamanı süzgeci: `storePlatformSchema.safeParse('HEPSIBURADA')`
 * sessizce `false` dönerdi ve `/commerce/hepsiburada/callback` "bilinmeyen
 * platform" diye 404 verirdi.
 *
 * `z.enum(StorePlatform)` (Zod 4) sabit nesneyi doğrudan alıyor. Artık
 * enum'a değer eklemek şemayı KENDİLİĞİNDEN genişletiyor; ikisinin sapması
 * yapısal olarak imkânsız.
 */
export const storePlatformSchema = z.enum(StorePlatform);

/**
 * Bağlantı başlatma.
 *
 * `shopDomain` SHOPIFY için ZORUNLU ve bu Shopify'a özgü bir tuhaflık değil,
 * OAuth'unun tanımı: izin ekranının adresi mağazanın kendi alan adında
 * (`https://magazam.myshopify.com/admin/oauth/authorize`). Merkezî bir
 * "Shopify ile bağlan" adresi YOK — kullanıcı mağazasını söylemeden akış
 * başlatılamaz.
 */
export const startStoreConnectSchema = z.object({
  platform: storePlatformSchema,
  /// `magazam.myshopify.com` ya da `magazam`. Normalizasyon serviste.
  shopDomain: z.string().trim().min(1).max(255).optional(),
  /// Akış bitince dönülecek panel rotası. Açık yönlendirme (open redirect)
  /// riski yüzünden yalnız `/` ile başlayan göreli yollar kabul ediliyor.
  returnTo: z
    .string()
    .max(500)
    .regex(/^\/(?!\/)/, 'returnTo yalnız göreli bir panel yolu olabilir')
    .optional(),
});
export type StartStoreConnect = z.infer<typeof startStoreConnectSchema>;

/**
 * MAĞAZA OAUTH GERİ DÖNÜŞ HATALARI — arayüzün beyaz listesi.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 Neden `OAUTH_REDIRECT_ERROR`a EKLENMEDİ, kardeşi olarak yazıldı
 * ═══════════════════════════════════════════════════════════════════════════
 * O tablo REKLAM bağlantısının dönüşünü anlatıyor ve dört kodu var. Mağaza
 * akışının iki kodu orada karşılıksız: `SHOP_MISMATCH` (kullanıcı A
 * mağazasıyla başlayıp B'den döndü) ve `SIGNATURE_INVALID` (Shopify'ın HMAC'i
 * tutmadı) — ikisi de reklam tarafında var olmayan kavramlar. Tek tabloya
 * doldurmak, reklam ekranına asla göremeyeceği iki hata kodu öğretmek olurdu.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 Buraya taşınmasının SEBEBİ: arayüz bu kodları TANIYAMIYORDU
 * ═══════════════════════════════════════════════════════════════════════════
 * Kodlar `apps/api/.../store-oauth.service.ts` içinde özel (`const
 * REDIRECT_ERROR`) yaşıyordu. Sonuç ölçüldü: geri dönüş
 * `?error=STORE_OAUTH_SHOP_MISMATCH` yazıyor, panel bu kodu beyaz listesinde
 * (`OAUTH_REDIRECT_ERROR`) bulamıyor ve genel "İşlem tamamlanamadı"ya
 * düşüyordu. Yani Shopify bağlama akışının HER hatası kullanıcıya aynı
 * cümleyi söylüyordu — mağazayı yanlış seçmekle imzanın tutmaması ayırt
 * edilemiyordu.
 *
 * Sözleşme burada olduğu için artık iki taraf da aynı listeyi okuyor: servis
 * bunu üretiyor, panel bunu bekliyor ve her koda çeviri yazılıyor
 * (`dashboard.stores.error.*`).
 */
export const STORE_OAUTH_REDIRECT_ERROR = {
  /// Kullanıcı platformun izin ekranında "izin verme" dedi. Hata DEĞİL, karar.
  STORE_OAUTH_DENIED: 'STORE_OAUTH_DENIED',
  /// Tek kullanımlık `state` bulunamadı ya da tüketilmişti (çift tıklama, geri tuşu).
  STORE_OAUTH_STATE_INVALID: 'STORE_OAUTH_STATE_INVALID',
  /// `state` yaşadı ama TTL'i doldu (10 dk) — kullanıcı izin ekranında bekledi.
  STORE_OAUTH_STATE_EXPIRED: 'STORE_OAUTH_STATE_EXPIRED',
  /// Shopify'ın HMAC'i tutmadı. Mağaza tarafına özgü; reklam akışında yok.
  STORE_OAUTH_SIGNATURE_INVALID: 'STORE_OAUTH_SIGNATURE_INVALID',
  /// Akış A mağazasıyla başladı, dönüş B mağazasından geldi.
  STORE_OAUTH_SHOP_MISMATCH: 'STORE_OAUTH_SHOP_MISMATCH',
  /// Jeton takası platformda başarısız oldu.
  STORE_OAUTH_EXCHANGE_FAILED: 'STORE_OAUTH_EXCHANGE_FAILED',
  /// Bu mağaza bu çalışma alanında zaten bağlı.
  STORE_ALREADY_CONNECTED: 'STORE_ALREADY_CONNECTED',
} as const;

export type StoreOAuthRedirectError =
  (typeof STORE_OAUTH_REDIRECT_ERROR)[keyof typeof STORE_OAUTH_REDIRECT_ERROR];

/**
 * ELLE SENKRON İSTEĞİNİN ÜÇ SONUCU — `STORE_OAUTH_REDIRECT_ERROR`ın kardeşi.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 Neden sözleşme: üç sonuç arayüzde AYNI görünüyordu — hiçbir şey
 * ═══════════════════════════════════════════════════════════════════════════
 * `POST /commerce/connections/:id/sync` 202 dönüyor ama gövdesi üç ayrı şey
 * söyleyebiliyor: iş kuyruğa girdi / soğumada / o mağazada senkron kapalı.
 * Kodlar `commerce.service.ts` içinde çıplak dize olarak yaşıyordu ve panel
 * gövdeyi hiç OKUMUYORDU (`stores-board.tsx` yalnız `router.refresh()`
 * çağırıyordu). Sonuç 18 Ağu 2026'da ölçüldü: kullanıcı düğmeye bastı, isteği
 * soğuma reddetti, ekranda hiçbir iz kalmadı ve "senkron çalışmıyor" diye
 * bildirildi — oysa senkron çalışmıştı, cevap görünmüyordu.
 *
 * Liste burada olduğu için iki taraf da aynı şeyi okuyor: servis bunu
 * üretiyor, panel her koda bir bildirim yazıyor (`dashboard.stores.sync.*`).
 * Kopya bir birlik yazmak, `ACTION_TYPES_WITH_EXECUTOR` dersinin aynısını
 * tekrarlamak olurdu — sözleşme tek yerde durur, ikinci kopya sessizce eskir.
 *
 * ⚠️ İkisi de HATA DEĞİL: kullanıcının istediği şey (senkron) ya zaten
 * sırada ya da bilinçli olarak kapatılmış. Bu yüzden uç 4xx dönmüyor ve
 * panelde `error` değil `info` tonuyla bildiriliyor.
 */
export const STORE_SYNC_SKIP_REASON = {
  /// Aynı mağaza için son 5 dakikada zaten elle senkron istendi
  /// (`STORE_MANUAL_COOLDOWN_SECONDS`). İş kuyrukta, ikincisi eklenmedi.
  COOLDOWN: 'COOLDOWN',
  /// Bağlantının `syncEnabled` alanı kapalı — düğme basıldı ama kullanıcı
  /// senkronu daha önce kendisi durdurmuş.
  SYNC_DISABLED: 'SYNC_DISABLED',
} as const;

export type StoreSyncSkipReason =
  (typeof STORE_SYNC_SKIP_REASON)[keyof typeof STORE_SYNC_SKIP_REASON];

/**
 * Elle senkron ucunun gövdesi.
 *
 * 🔴 `queued: true` "senkron bitti" DEĞİL, "iş kuyruğa girdi" demek. Worker
 * ayrıca kendi kilidine bakıyor ve o kilit tutuluyorsa işi `LOCKED` diye
 * atlıyor — yani `queued: true` alan bir istek bile veriyi hemen
 * tazelemeyebilir. Panel bu yüzden ilerlemeyi `/progress` ucundan izliyor.
 */
export interface StoreSyncTriggerResult {
  queued: boolean;
  reason?: StoreSyncSkipReason;
}

export const storeConnectionDtoSchema = z.object({
  id: z.string(),
  platform: storePlatformSchema,
  externalId: z.string(),
  displayName: z.string().nullable(),
  domain: z.string().nullable(),
  currency: z.string(),
  timezone: z.string(),
  status: connectionStatusSchema,
  /// Platform hatasının TEMİZLENMİŞ hâli. Ham metin jeton parçası
  /// taşıyabiliyor (`sanitizeProviderError`).
  lastError: z.string().nullable(),
  lastSyncedAt: z.string().nullable(),
  syncEnabled: z.boolean(),
  /// Webhook kurulabildi mi. `false` ise tazelik yalnız zamanlanmış
  /// senkrondan geliyor ve arayüz bunu söylemeli.
  webhooksActive: z.boolean(),
  productCount: z.number().int(),
  orderCount: z.number().int(),
  createdAt: z.string(),
});
export type StoreConnectionDto = z.infer<typeof storeConnectionDtoSchema>;

export const productListQuerySchema = listQuerySchema.extend({
  storeConnectionId: z.string().uuid().optional(),
  /// Tek bir mağaza değil, bir PLATFORMUN tamamı. `storeConnectionId`in yerine
  /// geçmiyor, yanında duruyor: kullanıcı çoğu zaman "Shopify tarafında ne
  /// var" diye soruyor, "hangi mağaza" diye değil — ve aynı platformda iki
  /// mağazası olabilir, bağlantı kimliği o soruyu tek başına cevaplamıyor.
  platform: storePlatformSchema.optional(),
  status: productStatusSchema.optional(),
  /// Başlık / SKU / satıcı içinde arar.
  q: z.string().trim().max(200).optional(),
  /// Stok eşiğinin altındakiler. `0` = tükenenler.
  maxInventory: z.coerce.number().int().min(0).optional(),
  /// Görseli olmayanlar — Adım 3'ün sağlık kuralının en ağır maddesi, ama
  /// kullanıcı Adım 1'de de sorabilmeli.
  missingImage: z.coerce.boolean().optional(),
});
export type ProductListQuery = z.infer<typeof productListQuerySchema>;

export const orderListQuerySchema = listQuerySchema.extend({
  storeConnectionId: z.string().uuid().optional(),
  /// Gerekçe ürün sorgusunda.
  platform: storePlatformSchema.optional(),
  financialStatus: orderFinancialStatusSchema.optional(),
  fulfillmentStatus: orderFulfillmentStatusSchema.optional(),
  /// `YYYY-MM-DD`, dahil.
  from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  to: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
});
export type OrderListQuery = z.infer<typeof orderListQuerySchema>;

export interface ProductDto {
  id: string;
  storeConnectionId: string;
  platform: StorePlatform;
  platformRef: string;
  title: string;
  handle: string | null;
  productType: string | null;
  vendor: string | null;
  tags: string[];
  status: ProductStatus;
  imageUrl: string | null;
  imageCount: number;
  onlineStoreUrl: string | null;
  /// Varyant sayısı ve fiyat aralığı — liste ekranı varyantları çekmeden
  /// bunu gösterebilsin diye özetlenmiş.
  variantCount: number;
  minPrice: number | null;
  maxPrice: number | null;
  /// Tüm varyantların toplam stoğu. `null` = hiçbir varyantta takip yok.
  totalInventory: number | null;
  publishedAt: string | null;
  updatedAt: string;
}

export interface OrderDto {
  id: string;
  storeConnectionId: string;
  platform: StorePlatform;
  platformRef: string;
  orderNumber: string;
  placedAt: string;
  currency: string;
  totalPrice: number;
  totalRefunded: number;
  financialStatus: OrderFinancialStatus;
  fulfillmentStatus: OrderFulfillmentStatus;
  cancelledAt: string | null;
  channel: string | null;
  lineCount: number;
  /// Satırlar YALNIZ tekil sipariş ucunda dolu. Liste ucunda `undefined`:
  /// 50 siparişin satırlarını da göndermek yükü üçe katlardı ve liste
  /// ekranında hiçbiri gösterilmiyor.
  lines?: OrderLineDto[];
}

export interface OrderLineDto {
  id: string;
  /// Ürün silinmişse `null` — satırın kendisi yine burada ve `title` dolu.
  productId: string | null;
  variantId: string | null;
  title: string;
  sku: string | null;
  quantity: number;
  unitPrice: number;
  discount: number;
  totalPrice: number;
}

/// Mağaza bağlantısının sağlık özeti — panelde tek uyarı kutusunda gösteriliyor.
/**
 * `GET /commerce/platforms` yanıtının TEK satırı.
 *
 * 🔴 Bu tip arayüz için var: olmasaydı `apps/web` yanıtın şeklini ELLE
 * yazardı ve sunucu bir alan eklediğinde/adını değiştirdiğinde hiçbir yerde
 * derleme hatası çıkmazdı — "sözleşme vs gerçeklik" hatasının tam olarak
 * doğduğu yer.
 *
 * ⚠️ `PlatformCard` (reklam tarafı) ile AYNI DEĞİL ve birleştirilmemeli:
 * orada `connectionCount` ve reklam yetenekleri var, burada `authMode` ve
 * `credentialFields`. Ortak alan yalnız üç durum bayrağı.
 */
export interface StorePlatformCard {
  platform: StorePlatform;
  /// Adapter yazıldı mı. `false` → "yakında".
  implemented: boolean;
  /// Yazıldı VE env tamam mı. `implemented && !configured` → "yönetici
  /// yapılandırmalı" (üç durum, gerekçe `commerce.service.ts`te).
  configured: boolean;
  /// ⚠️ Anahtar ADLARI, DEĞERLERİ değil.
  missingEnvKeys: string[];
  unavailableReasonKey: string | null;
  /// Arayüz hangi akışı çizeceğine BUNA bakıyor — platform adına DEĞİL.
  authMode: StoreAuthMode;
  /// `authMode: 'apiKey'` ise formun alanları. OAuth'ta boş.
  credentialFields: StoreCredentialField[];
  /**
   * `authMode: 'oauth'` ise akışı BAŞLATMADAN ÖNCE sorulacak tek alan.
   * `null` → hiçbir şey sorulmuyor, düğme doğrudan izin ekranına götürüyor.
   */
  oauthStartField: StoreOAuthStartField | null;
}

export interface StoreHealthDto {
  connectionId: string;
  status: ConnectionStatus;
  lastSyncedAt: string | null;
  /// Son senkrondan bu yana geçen dakika. `null` = hiç senkron koşmadı.
  staleMinutes: number | null;
  lastError: string | null;
  webhooksActive: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// Genişleme Adım 2 — satıcı anahtarıyla bağlanan pazaryerleri
// ═══════════════════════════════════════════════════════════════════════════
//
// Trendyol · İkas · Ticimax. Üçünün de kimlik modeli Shopify'dan TABAN TABANA
// farklı ve fark tek bir cümlede özetlenebiliyor: **uygulama düzeyi anahtar
// YOK.** Satıcı kendi panelinden bir anahtar üretiyor ve onu bize yapıştırıyor.
//
//   Shopify  → biz uygulamayız, `.env`de anahtarımız var, kullanıcı İZİN veriyor
//   Bu üçü   → uygulama diye bir şey yok, kullanıcı ANAHTAR veriyor
//
// Ölçüldü (13 Ağu 2026, resmî dokümandan): Trendyol satıcı panelinde
// "Entegrasyon Bilgileri", İkas panelinde "Private App", Ticimax panelinde
// "WS Yetki Kodu". Üçünde de Mentorivo'nun kaydolduğu bir yer YOK — yani
// `STORE_PLATFORM_ENV_KEYS`teki boş diziler kalıcı olarak doğru.

/**
 * Bir mağaza platformuna nasıl bağlanılıyor.
 *
 * Arayüz bu değere bakarak KARARINI veriyor: `oauth` ise "Bağlan" düğmesi
 * (tarayıcı platforma gider), `apiKey` ise bir FORM (kullanıcı anahtarını
 * yapıştırır). İkisi aynı akış değil ve arayüzün bunu platform adına bakarak
 * tahmin etmesi, `if (platform === 'SHOPIFY')` dalını arayüze taşımak olurdu.
 */
export type StoreAuthMode = 'oauth' | 'apiKey';

/// ⚠️ Adım 11'de eklenen yedi platformun kipi de BURADA — adapter'ı olmayanlar
/// dâhil. Sebep: bu harita `Record<StorePlatform, …>` ve derleyici eksik
/// anahtarı zaten yakalıyor; değeri "bilmiyorum" diye boş bırakmanın yolu yok.
/// Değerler uydurma değil, eski APEX projesindeki adapter'ların FİİLEN
/// kullandığı kipten alındı (Amazon ve Etsy'de OAuth controller'ı vardı,
/// kalan dördünde anahtar formu).
export const STORE_PLATFORM_AUTH_MODE: Record<StorePlatform, StoreAuthMode> = {
  SHOPIFY: 'oauth',
  TRENDYOL: 'apiKey',
  IKAS: 'apiKey',
  TICIMAX: 'apiKey',
  HEPSIBURADA: 'apiKey',
  N11: 'apiKey',
  AMAZON: 'oauth',
  ETSY: 'oauth',
  WOOCOMMERCE: 'apiKey',
  TSOFT: 'apiKey',
  IDEASOFT: 'apiKey',
};

/**
 * Anahtar formundaki TEK bir alan.
 *
 * 🔴 Bu liste neden sunucuda: üç platformun formu birbirinden farklı
 * (Ticimax'ta `apiSecret` YOK, İkas'ta `sellerId` mağaza adı, Trendyol'da
 * satıcı numarası) ve arayüzün bunu bilmesinin iki yolu var — ya burada
 * veriden okur, ya kendi içinde üç dallı bir `switch` taşır. İkincisi
 * "platform kavramı üst katmana sızmaz" kuralının doğrudan ihlali olurdu.
 *
 * `labelKey` / `helpKey` i18n ANAHTARI, düz metin değil — `unavailableReasonKey`
 * ile aynı desen ve aynı gerekçe.
 */
export interface StoreCredentialField {
  name: 'apiKey' | 'apiSecret' | 'sellerId';
  required: boolean;
  /// `true` ise arayüz `type="password"` çizer ve değer bir daha GÖSTERİLMEZ.
  secret: boolean;
  labelKey: string;
  /// Kullanıcı bu değeri hangi ekrandan alıyor — form altındaki yardım satırı.
  helpKey: string;
}

/**
 * Platform başına anahtar formu — ÖLÇÜLDÜ, tahmin değil.
 *
 * ⚠️ `sellerId` alanının anlamı platform başına DEĞİŞİYOR ve bu bilinçli bir
 * tercih: üç ayrı alan adı uydurmak (`sellerId` / `storeName` / `storeDomain`)
 * `apiKeyCredentialsSchema`ya iki yeni alan eklemek demekti ve o şema
 * `store-credentials.spec.ts` tarafından korunuyor. Tek alan, tek anlam:
 * **"hangi mağaza"**.
 *
 *   TRENDYOL → satıcı kimliği (`sellerId`/`supplierId`, sayı) — URL yolunda
 *   IKAS     → mağaza adı (`<magaza>.myikas.com`) — jeton adresinde
 *   TICIMAX  → mağazanın KENDİ alan adı — taban adres bundan kuruluyor
 *
 * Shopify listede YOK ve olmamalı: onun akışı OAuth ve `shopDomain`
 * `startStoreConnectSchema`da zaten var.
 */
/**
 * OAUTH AKIŞINI BAŞLATMADAN ÖNCE SORULAN TEK ALAN.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 Neden var: `connect/start` bu değeri ZORUNLU tutuyordu, arayüz ise HİÇ
 * göndermiyordu
 * ═══════════════════════════════════════════════════════════════════════════
 * 18 Ağu 2026'da ölçüldü: `stores-board.tsx` `startConnect(card.platform)`
 * diye çağırıyordu — `shopDomain` yok. Sunucu ise onsuz `storeInvalidDomain()`
 * fırlatıyordu. Yani panelden **Shopify dâhil** hiçbir OAuth mağazası
 * bağlanamıyordu; düğme her zaman 400 alıyordu.
 *
 * Eksik olan şey bir doğrulama değil bir EKRAN: kullanıcıya sorulmayan bir
 * değer gönderilemez. Bu tablo o ekranı VERİDEN çiziyor —
 * `STORE_CREDENTIAL_FIELDS`in OAuth kardeşi ve aynı gerekçe: arayüzde
 * `if (platform === 'AMAZON')` dalı olmasın.
 *
 * ⚠️ Alanın `connect/start` gövdesindeki adı platformdan bağımsız olarak
 * `shopDomain` — ve bu ad Amazon'da YANILTICI (orada değer bir bölge).
 * Değiştirilmedi: taşıma kanalının adı sözleşmenin parçası ve
 * `startStoreConnectSchema` bu adla yazılı. Anlamın platforma göre
 * değiştiği `AmazonConnector.getAuthUrl` başlığında da yazılı.
 */
export interface StoreOAuthStartField {
  /// `startStoreConnectSchema` gövdesindeki alan. Bugün tek değer var.
  name: 'shopDomain';
  labelKey: string;
  helpKey: string;
  /**
   * Kapalı liste. Doluysa arayüz SEÇİM çizer, serbest metin değil.
   *
   * 🔴 Amazon bölgesi için zorunlu: değer üç şeyden biri (`na`/`eu`/`fe`) ve
   * yanlışı sessiz. Yanlış bölgeye giden istek hata değil BOŞ LİSTE döndürüyor
   * — yani "0 ürün, başarılı" diyen bir senkron. Serbest metin bırakmak, bu
   * sessiz kaybı kullanıcının yazım hatasına açık hâle getirmek olurdu.
   */
  options?: { value: string; labelKey: string }[];
}

export const STORE_OAUTH_START_FIELD: Record<StorePlatform, StoreOAuthStartField | null> = {
  /// Shopify'da izin ekranı mağazanın KENDİ alan adında yaşıyor; merkezî bir
  /// "Shopify ile bağlan" adresi yok. Serbest metin, çünkü kapalı liste
  /// mümkün değil.
  SHOPIFY: {
    name: 'shopDomain',
    labelKey: 'commerce.field.shopifyDomain',
    helpKey: 'commerce.help.shopifyDomain',
  },
  /// Amazon'da sorulan şey BÖLGE — izin ekranının adresi de veri ucu da
  /// bölgeye göre değişiyor (`AmazonConnector.getAuthUrl` başlığı).
  AMAZON: {
    name: 'shopDomain',
    labelKey: 'commerce.field.amazonRegion',
    helpKey: 'commerce.help.amazonRegion',
    options: [
      { value: 'eu', labelKey: 'commerce.option.amazonRegionEu' },
      { value: 'na', labelKey: 'commerce.option.amazonRegionNa' },
      { value: 'fe', labelKey: 'commerce.option.amazonRegionFe' },
    ],
  },
  /// Etsy'de sorulacak bir şey YOK: izin ekranı tek merkezî adreste ve mağaza
  /// kimliği jeton takasından sonra öğreniliyor (`getAuthUrl` üçüncü
  /// parametreyi `_shopDomain` diye adlandırıp kullanmıyor).
  ETSY: null,
  /// Kalan sekizi `authMode: 'apiKey'` — bu tablo onlarda hiç okunmuyor.
  /// `Record` zorunlu kıldığı için `null` yazılı; "sorulmuyor" demek.
  TRENDYOL: null,
  IKAS: null,
  TICIMAX: null,
  HEPSIBURADA: null,
  N11: null,
  WOOCOMMERCE: null,
  TSOFT: null,
  IDEASOFT: null,
};

export const STORE_CREDENTIAL_FIELDS: Record<StorePlatform, StoreCredentialField[]> = {
  SHOPIFY: [],
  TRENDYOL: [
    {
      name: 'sellerId',
      required: true,
      secret: false,
      labelKey: 'commerce.field.trendyolSellerId',
      helpKey: 'commerce.help.trendyolIntegrationInfo',
    },
    {
      name: 'apiKey',
      required: true,
      secret: true,
      labelKey: 'commerce.field.apiKey',
      helpKey: 'commerce.help.trendyolIntegrationInfo',
    },
    {
      name: 'apiSecret',
      required: true,
      secret: true,
      labelKey: 'commerce.field.apiSecret',
      helpKey: 'commerce.help.trendyolIntegrationInfo',
    },
  ],
  IKAS: [
    {
      name: 'sellerId',
      required: true,
      secret: false,
      labelKey: 'commerce.field.ikasStoreName',
      helpKey: 'commerce.help.ikasPrivateApp',
    },
    {
      name: 'apiKey',
      required: true,
      secret: true,
      labelKey: 'commerce.field.ikasClientId',
      helpKey: 'commerce.help.ikasPrivateApp',
    },
    {
      name: 'apiSecret',
      required: true,
      secret: true,
      labelKey: 'commerce.field.ikasClientSecret',
      helpKey: 'commerce.help.ikasPrivateApp',
    },
  ],
  TICIMAX: [
    {
      name: 'sellerId',
      required: true,
      secret: false,
      labelKey: 'commerce.field.ticimaxDomain',
      helpKey: 'commerce.help.ticimaxAuthCode',
    },
    {
      /// 🔴 Ticimax'ta `apiSecret` YOK — tek bir "WS Yetki Kodu" var ve o
      /// `apiKey`e giriyor. İsmi ("Üye Kodu") yanıltıcı: müşteri kimliği
      /// değil, dokümanın kendi tanımıyla "servis sağlayıcısı tarafından
      /// verilen ŞİFRE". Sır olarak işaretli olması bu yüzden.
      name: 'apiKey',
      required: true,
      secret: true,
      labelKey: 'commerce.field.ticimaxAuthCode',
      helpKey: 'commerce.help.ticimaxAuthCode',
    },
  ],

  // ── Adım 11 ──────────────────────────────────────────────────────────────
  //
  // 🔴 Hepsiburada'nın üç alanı ÖLÇÜLDÜ (eski projenin çalışan bağlantı
  // formundan): satıcı numarası + API kullanıcı adı + API şifresi. Üçü de
  // mevcut `apiKeyCredentialsSchema`ya yeni kolon istemeden oturuyor —
  // `sellerId`in "üç anlam taşıyan tek alan" olması bir kez daha işe yaradı.
  HEPSIBURADA: [
    {
      name: 'sellerId',
      required: true,
      secret: false,
      labelKey: 'commerce.field.hepsiburadaMerchantId',
      helpKey: 'commerce.help.hepsiburadaMerchantPanel',
    },
    {
      /// Hepsiburada'da bu bir "key" değil KULLANICI ADI (Basic auth'un sol
      /// tarafı). `apiKey` alanına giriyor çünkü şema alan adını değil ROLÜ
      /// tarif ediyor; Ticimax'ta da "WS Yetki Kodu" aynı alanda oturuyor.
      /// Sır DEĞİL ama sır alanında yaşıyor — etiket bunu düzeltiyor.
      name: 'apiKey',
      required: true,
      secret: false,
      labelKey: 'commerce.field.hepsiburadaUsername',
      helpKey: 'commerce.help.hepsiburadaMerchantPanel',
    },
    {
      name: 'apiSecret',
      required: true,
      secret: true,
      labelKey: 'commerce.field.hepsiburadaPassword',
      helpKey: 'commerce.help.hepsiburadaMerchantPanel',
    },
  ],

  /**
   * 🔴 ALTISI DA BOŞ — ve boş bırakmak burada tek dürüst seçenek.
   *
   * Adapter'ları yazılmadı, `isImplemented()` altısına da `false` diyor, yani
   * arayüz bu formu HİÇ çizmiyor. Eski projenin DTO'larından alan listesi
   * üretmek mümkündü ama yapılmadı: o adapter'ların hiçbiri gerçek bir hesaba
   * bağlanmamış, yani alan listeleri de ölçüm değil TAHMİN. Buraya
   * yazsaydık ölçülmüş üç platformla aynı görünürlükte, aynı güvenle duran
   * altı tahmin olurdu.
   *
   * Bu, "sözleşme vs gerçeklik" hatasının tam tersi yönü: orada ölü yüzey
   * ELDE VAR sanılıp yazılmıştı; burada yokluğu açıkça ilan ediliyor.
   * Adapter yazıldığı gün alanlar da ÖLÇÜLEREK gelir.
   */
  /**
   * n11 — 🔴 `sellerId` İSTENMİYOR ve bu bir eksiklik değil ölçüm sonucu.
   *
   * n11'de mağaza numarası diye bir alan yok; mağaza referansı App Key'den
   * türetiliyor (`deriveStoreRef`). Forma boş bir "Mağaza numarası" kutusu
   * koymak, kullanıcıya bulamayacağı bir şeyi sordurmak olurdu. Türetilmiş
   * değer yine de kayda yazılıyor — `openMarketplaceReader` `sellerId`
   * bekliyor ve sözleşme bozulmuyor.
   */
  N11: [
    {
      name: 'apiKey',
      required: true,
      secret: true,
      labelKey: 'commerce.field.n11AppKey',
      helpKey: 'commerce.help.n11ApiKeys',
    },
    {
      name: 'apiSecret',
      required: true,
      secret: true,
      labelKey: 'commerce.field.n11AppSecret',
      helpKey: 'commerce.help.n11ApiKeys',
    },
  ],

  /**
   * WooCommerce · T-Soft · İdeaSoft — üçünde de `sellerId` MAĞAZANIN KENDİ
   * ALAN ADI ve taban adres ondan kuruluyor.
   *
   * 🔴 Bu üçü, ürünün EN BÜYÜK SSRF yüzeyi: diğer platformlarda hedef sabit
   * (`apigw.trendyol.com`), burada hedefi kullanıcı yazıyor. Süzgeç
   * istemcilerin `buildUrl`i İÇİNDE çalışıyor (çağırana emanet edilmedi) ve
   * yalnız dize kontrolü değil — ad çözülüp iç ağ adresleri de eleniyor,
   * 3xx yönlendirme izlenmiyor. Yardım metinleri bu kısıtı kullanıcıya
   * ÖNCEDEN söylüyor ki reddedilen adres bir sürpriz olmasın.
   */
  WOOCOMMERCE: [
    {
      name: 'sellerId',
      required: true,
      secret: false,
      labelKey: 'commerce.field.woocommerceStoreUrl',
      helpKey: 'commerce.help.woocommerceRestApi',
    },
    {
      name: 'apiKey',
      required: true,
      secret: true,
      labelKey: 'commerce.field.woocommerceConsumerKey',
      helpKey: 'commerce.help.woocommerceRestApi',
    },
    {
      name: 'apiSecret',
      required: true,
      secret: true,
      labelKey: 'commerce.field.woocommerceConsumerSecret',
      helpKey: 'commerce.help.woocommerceConsumerSecretOnce',
    },
  ],
  TSOFT: [
    {
      name: 'sellerId',
      required: true,
      secret: false,
      labelKey: 'commerce.field.tsoftDomain',
      helpKey: 'commerce.help.tsoftApiUser',
    },
    {
      name: 'apiKey',
      required: true,
      secret: false,
      labelKey: 'commerce.field.tsoftUsername',
      helpKey: 'commerce.help.tsoftApiUser',
    },
    {
      name: 'apiSecret',
      required: true,
      secret: true,
      labelKey: 'commerce.field.tsoftPassword',
      helpKey: 'commerce.help.tsoftApiUser',
    },
  ],
  IDEASOFT: [
    {
      name: 'sellerId',
      required: true,
      secret: false,
      labelKey: 'commerce.field.ideasoftDomain',
      helpKey: 'commerce.help.ideasoftApiUser',
    },
    {
      name: 'apiKey',
      required: true,
      secret: true,
      labelKey: 'commerce.field.ideasoftClientId',
      helpKey: 'commerce.help.ideasoftApiUser',
    },
    {
      name: 'apiSecret',
      required: true,
      secret: true,
      labelKey: 'commerce.field.ideasoftClientSecret',
      helpKey: 'commerce.help.ideasoftApiUser',
    },
  ],

  /**
   * 🔴 Amazon ve Etsy BOŞ — Shopify ile aynı gerekçe, farklı sebep değil.
   *
   * İkisi de `authMode: 'oauth'`: kullanıcı forma anahtar yazmıyor, izin
   * ekranına gidiyor. `store-credentials.service.ts` zaten `storeAuthModeMismatch`
   * ile anahtar formuna düşmelerini engelliyor.
   *
   * ⚠️ Ayrıca ikisi de bugün `isImplemented: false` — adapter'ları yazıldı ama
   * jeton yenileme hattı yok (gerekçe `store-connector.registry.ts`). Yani bu
   * boş diziler iki ayrı sebeple boş ve ikisi de doğru.
   */
  AMAZON: [],
  ETSY: [],
};

/**
 * Anahtarla bağlanma isteği.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 BU UÇ KAYDETMEDEN ÖNCE PLATFORMA GİDİYOR — genişleme planının açık şartı
 * ═══════════════════════════════════════════════════════════════════════════
 * "Anahtar doğrulaması kaydetmeden ÖNCE yapılsın: yanlış anahtarı kaydedip
 * ilk senkronda öğrenmek, kullanıcıya 'bağlandı' deyip sessizce
 * çalışmamaktır." Bu yüzden akış OAuth'unkinin tersi: orada kayıt geri
 * dönüşte kuruluyor, burada kayıt ancak platform "evet" dedikten SONRA
 * kuruluyor. Reddedilen anahtar için `STORE_CREDENTIALS_REJECTED` dönüyor ve
 * veritabanına HİÇBİR ŞEY yazılmıyor.
 *
 * ⚠️ `apiSecret` opsiyonel çünkü Ticimax'ta karşılığı YOK. Zorunluluğu
 * `STORE_CREDENTIAL_FIELDS` tarif ediyor ve doğrulaması adapter'ın içinde —
 * burada zorunlu yapsaydık Ticimax kullanıcısı olmayan bir alanı doldurmak
 * zorunda kalırdı.
 */
export const connectStoreWithCredentialsSchema = z.object({
  platform: storePlatformSchema,
  /**
   * "Hangi mağaza" — anlamı platform başına değişiyor, bkz.
   * `STORE_CREDENTIAL_FIELDS`.
   *
   * Sır DEĞİL ama kimlik bilgisi blob'unda yaşıyor: connector'lar DURUMSUZ ve
   * doğrulama kayıt AÇILMADAN ÖNCE koşuyor — yani bu değerin okunabileceği
   * bir satır henüz yok. `apiKeyCredentialsSchema.sellerId` yorumu bunu
   * Adım 1'de öngörmüştü.
   */
  sellerId: z.string().trim().min(1).max(255),
  apiKey: z.string().trim().min(1).max(2000),
  apiSecret: z.string().trim().min(1).max(2000).optional(),
});
export type ConnectStoreWithCredentials = z.infer<typeof connectStoreWithCredentialsSchema>;
